"use client";

import { usePathname, useRouter } from "next/navigation";

import { X } from "lucide-react";
import { useForm } from "react-hook-form";

import { Tables } from "@kit/supabase/database";
import { Button } from "@kit/ui/button";
import { Form, FormControl, FormField, FormItem } from "@kit/ui/form";
import { If } from "@kit/ui/if";
import { Input } from "@kit/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@kit/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@kit/ui/tooltip";
import { Trans } from "@kit/ui/trans";

interface IssuesFiltersProps {
  filters: {
    query: string;
    status: Tables<"issues">["status"] | "all";
    type: Tables<"issues">["type"] | "all";
    sortBy: "created_at" | "votes";
  };

  displayUnderReview: boolean;
}

export function IssuesFilters(props: IssuesFiltersProps) {
  const router = useRouter();
  const pathName = usePathname();

  const form = useForm<IssuesFiltersProps["filters"]>({
    defaultValues: {
      query: props.filters.query ?? "",
      status: props.filters.status ?? "all",
      sortBy: props.filters.sortBy ?? "created_at",
      type: props.filters.type ?? "all",
    },
    mode: "onChange",
  });

  const onSubmit = form.handleSubmit((values) => {
    const query = new URLSearchParams({
      query: values.query,
      status: values.status,
      sort_by: values.sortBy,
      type: values.type,
    });

    router.push(`${pathName}?${query.toString()}`);
  });

  const hasFilters =
    props.filters.status !== "all" ||
    props.filters.query !== "" ||
    props.filters.sortBy !== "created_at" ||
    props.filters.type !== "all";

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className={"flex flex-col gap-1 lg:flex-row lg:justify-end lg:gap-2.5"}
      >
        <FormField
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      form.setValue(
                        field.name,
                        value as IssuesFiltersProps["filters"]["type"],
                        {
                          shouldValidate: true,
                        },
                      );

                      void onSubmit();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={"Type"} />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value={"all"}>
                        <Trans i18nKey={"roadmap:all"} defaults={"All"} />
                      </SelectItem>

                      <SelectItem value={"feature"}>
                        <Trans
                          i18nKey={"roadmap:featureRequests"}
                          defaults={"Feature Requests"}
                        />
                      </SelectItem>

                      <SelectItem value={"bug"}>
                        <Trans
                          i18nKey={"roadmap:bugReports"}
                          defaults={"Bug Reports"}
                        />
                      </SelectItem>
                      <SelectItem value={"improvement"}>
                        <Trans
                          i18nKey={"roadmap:improvement"}
                          defaults={"Improvement"}
                        />
                      </SelectItem>
                      <SelectItem value={"task"}>
                        <Trans i18nKey={"roadmap:task"} defaults={"Task"} />
                      </SelectItem>
                      <SelectItem value={"question"}>
                        <Trans
                          i18nKey={"roadmap:question"}
                          defaults={"Question"}
                        />
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            );
          }}
          name={"type"}
        />

        <FormField
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      form.setValue(
                        field.name,
                        value as IssuesFiltersProps["filters"]["sortBy"],
                        {
                          shouldValidate: true,
                        },
                      );

                      void onSubmit();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={"Sort By"} />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value={"created_at"}>
                        <Trans
                          i18nKey={"roadmap:recentlyCreated"}
                          defaults={"Recently Created"}
                        />
                      </SelectItem>

                      <SelectItem value={"votes"}>
                        <Trans
                          i18nKey={"roadmap:mostVotes"}
                          defaults={"Most Votes"}
                        />
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            );
          }}
          name={"sortBy"}
        />

        <FormField
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      form.setValue(
                        field.name,
                        value as IssuesFiltersProps["filters"]["status"],
                        {
                          shouldValidate: true,
                        },
                      );

                      void onSubmit();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={"Status"} />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value={"all"}>
                        <Trans
                          i18nKey={"roadmap:allStatuses"}
                          defaults={"AllStatuses"}
                        />
                      </SelectItem>

                      <If condition={props.displayUnderReview}>
                        <SelectItem value={"under_review"}>
                          <Trans
                            i18nKey={"roadmap:underReview"}
                            defaults={"Under Review"}
                          />
                        </SelectItem>
                      </If>

                      <SelectItem value={"planned"}>
                        <Trans
                          i18nKey={"roadmap:planned"}
                          defaults={"Planned"}
                        />
                      </SelectItem>

                      <SelectItem value={"in_progress"}>
                        <Trans
                          i18nKey={"roadmap:inProgress"}
                          defaults={"In Progress"}
                        />
                      </SelectItem>

                      <SelectItem value={"completed"}>
                        <Trans
                          i18nKey={"roadmap:completed"}
                          defaults={"Completed"}
                        />
                      </SelectItem>

                      <SelectItem value={"declined"}>
                        <Trans
                          i18nKey={"roadmap:declined"}
                          defaults={"Declined"}
                        />
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            );
          }}
          name={"status"}
        />

        <FormField
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    className={"w-full min-w-64"}
                    placeholder={"Search..."}
                  />
                </FormControl>
              </FormItem>
            );
          }}
          name={"query"}
        />

        <If condition={hasFilters}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size={"icon"}
                  type={"button"}
                  variant={"ghost"}
                  onClick={() => {
                    form.reset();
                    void onSubmit();
                  }}
                >
                  <X className={"h-4"} />
                </Button>
              </TooltipTrigger>

              <TooltipContent>
                <Trans
                  i18nKey={"roadmap:clearFilters"}
                  defaults={"Clear Filters"}
                />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </If>
      </form>
    </Form>
  );
}
